import { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { isEmpty, kebabCase } from "lodash";
import { addDays } from "date-fns";

import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { defaultComponents } from "@/components/dynamic/DynamicFormContainer";
import { AlertModal } from "@/components/modals";
import { BoxedContent } from "@/components/common";
import showToast from "@/utils/toast/helpers";
import {
  DynamicFormContainer,
  CustomActionDropdown,
} from "@/components/dynamic";
import {
  getDynamicObjectRecords,
  getDynamicObjectRecordById,
} from "@/api/dynamic/dynamicObjectNameApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { calculateTax, formatDecimalValues } from "@/utils/helpers";
import { TableWithCheckbox } from "@/components/finance/account-receivables";
import {
  getPurchaseOrderTableColumns,
  getExpensePaymentTableColumns,
} from "@/components/finance/account-receivables/tableWithCheckboxData";
import { useCompanyAccount, useTaskRedirect, useModal } from "@/hooks";

const formatGrn = (grn) => {
  const data = {
    purchaseOrder: {
      label: grn.purchaseOrder.number,
      value: grn.purchaseOrder.id,
    },
    supplier: {
      label: grn.supplier.name,
      value: grn.supplier.id,
      ...grn.supplier,
    },
  };

  return data;
};

const filterPurchaseOrderItemDetail = (purchaseOrderItemDetail) => {
  const { invoiceStatus, receivedQuantity, invoicedQuantity } =
    purchaseOrderItemDetail;
  const remainingQuantity =
    Number(receivedQuantity || 0) - Number(invoicedQuantity || 0);

  if (remainingQuantity <= 0) {
    return false;
  }

  if (invoiceStatus === "Generated") {
    return false;
  }

  return true;
};

const formatPurchaseOrder = (purchaseOrder, expenseAccount, gRNId) => {
  const {
    id, number, supplier, currency, company, itemDetail, discountType
  } =
    purchaseOrder;

  const data = {
    key: id,
  };

  if (gRNId) {
    data.initiationType = {
      label: "P2p",
      value: "P2p",
    };
  } else {
    data.initialType = {
      label: "Purchase Invoice",
      value: "PurchaseInvoice",
    };
  }

  data.purchaseOrder = {
    label: number,
    value: id,
  };

  const { tRN, name, id: supplierId } = supplier;

  data.supplier = {
    label: name,
    value: supplierId,
    ...supplier,
  };

  data.tRN = tRN;

  data.currency = {
    label: currency?.name,
    value: currency?.id,
  };

  data.company = {
    label: company?.name,
    value: company?.id,
  };

  data.itemDetail = itemDetail
    .filter(filterPurchaseOrderItemDetail)
    .map((lineItem) => {
      const {
        item,
        rate,
        invoicedQuantity,
        receivedQuantity,
        discount: lineItemDiscount,
        amount: lineItemAmount,
        tax,
        costCenter,
      } = lineItem;

      const remainingQuantity =
        Number(receivedQuantity) - Number(invoicedQuantity || 0);
      const amount = formatDecimalValues(remainingQuantity * rate);

      const discountRatio = lineItemDiscount / lineItemAmount;
      const discountAmount = formatDecimalValues(amount * discountRatio);

      let discountedAmount = amount - discountAmount;
      let taxAmount = 0;
      if (tax && !isEmpty(tax)) {
        const parameters = {
          amount: discountedAmount,
          amountOfTax: { value: "Exclusive" },
          tax: { ...tax, label: tax?.name, value: tax?.id },
          taxAmount: "",
        };
        const {
          taxAmount: calculatedTaxAmount,
          principalAmount: discountedPrincipalAmount,
        } = calculateTax(parameters);
        taxAmount = calculatedTaxAmount;
        discountedAmount = discountedPrincipalAmount;
      }

      const totalAmount = Number(discountedAmount) + Number(taxAmount);

      const lineItemData = {
        ...lineItem,
        remainingQuantity,
        quantity: remainingQuantity,
        amount,
        discount: discountAmount,
        discountedAmount,
        taxAmount,
        totalAmount,
        purchaseOrderItemDetailId: lineItem.id,
      };

      lineItemData.item = {
        ...item,
        label: item?.itemName,
        value: item?.id,
      };

      lineItemData.account = expenseAccount;

      if (!isEmpty(costCenter)) {
        lineItemData.costCenter = {
          lookupObjectName: "CostCenter",
          label: costCenter.name,
          value: costCenter.id,
        };
      }

      if (!isEmpty(tax)) {
        lineItemData.tax = {
          label: tax.name,
          value: tax.id,
        };
      }

      return lineItemData;
    });

  data.discount = data.itemDetail.reduce(
    (prevValue, currentValue) =>
      Number(prevValue) + Number(currentValue.discount || 0),
    0
  );
  data.subtotal = data.itemDetail.reduce(
    (prevValue, currentValue) =>
      Number(prevValue) + Number(currentValue.amount || 0),
    0
  );
  data.discountedSubtotal = data.itemDetail.reduce(
    (prevValue, currentValue) =>
      Number(prevValue) + Number(currentValue.discountedAmount || 0),
    0
  );
  data.taxAmount = data.itemDetail.reduce(
    (prevValue, currentValue) =>
      Number(prevValue) + Number(currentValue.taxAmount || 0),
    0
  );
  data.total = data.itemDetail.reduce(
    (prevValue, currentValue) =>
      Number(prevValue) + Number(currentValue.totalAmount || 0),
    0
  );
  data.openBalance = data.total;
  data.discountType = {
    label: discountType,
    value: discountType,
  };

  data.status = {
    label: "Pending",
    value: "Pending",
  };

  return data;
};

const purchaseOrderInitialState = {
  itemDetail: [],
  subtotal: "",
  discount: "",
  discountType: null,
  total: "",
};

const calculateDiscountAmount = (
  discountType,
  discount,
  itemAmount,
  noOfItems
) => {
  if (discountType === "Amount") {
    return parseFloat(discount / noOfItems) || 0;
  }

  if (discountType === "Percentage") {
    return (parseFloat(itemAmount) * parseFloat(discount || 0)) / 100 || 0;
  }
  return 0;
};

const formatPayment = (payments) =>
  payments.map((item) => ({
    ...item,
    advanceAmount: Number(item?.total) - Number(item?.adjustedAmount ?? 0),
    adjustmentAmount: Number(item?.total) - Number(item?.adjustedAmount ?? 0),
    key: item.number,
    isSelected: false,
  }));

function HeaderRightContent({
  openModal,
  isPaymentSelected,
  setIsPaymentSelected,
  showButton,
  ...rest
}) {
  const onPaymentClick = () => {
    setIsPaymentSelected(true);
    openModal();
  };

  const onPurchaseOrderClick = () => {
    setIsPaymentSelected(false);
    openModal();
  };
  const actions = [
    {
      title: "Select Purchase Orders",
      onClick: () => {
        onPurchaseOrderClick();
      },
      icon: "document-icon",
    },
    {
      title: "Select Advance Payments",
      onClick: () => {
        onPaymentClick();
      },
      icon: "bag-icon",
    },
  ];
  return (
    <defaultComponents.HeaderRightContent {...rest}>
      {showButton ? (
        <div className="action-cell">
          <CustomActionDropdown
            actions={actions}
            trigger={(
              <div className="action-dropdown-trigger">
                <span className="text">Actions</span>
                <span className="material-icons-outlined">expand_more</span>
              </div>
            )}
            testId="Add-GL-Action-Button"
          />
        </div>
      ) : null}
    </defaultComponents.HeaderRightContent>
  );
}

function PurchaseInvoiceForm() {
  const ref = useRef(null);
  const [state, setState] = useState({});
  const [toastId, setToastId] = useState();
  const [initialState, setInitialState] = useState({});
  const [paymentDetailsTableData, setPaymentDetailsTableData] = useState([]);
  const [purchaseOrderTableData, setPurchaseOrderTableData] = useState([]);
  const [isPaymentSelected, setIsPaymentSelected] = useState(false);
  const { isOpen, closeModal, openModal } = useModal(false);
  const { redirect } = useTaskRedirect();
  const [searchParams] = useSearchParams();
  const goodsReceiptNote = searchParams.get("goodsReceiptNote");
  const purchaseOrderId = searchParams.get("purchaseOrder");
  const isCreatingPIFromPO = Boolean(purchaseOrderId);

  const { data: paymentData, isLoading } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("PaymentObjectName")),
      "PartiallyAdjusted,Posted",
      state?.supplier?.value,
    ],
    () =>
      getDynamicObjectRecords(
        dynamicObjectMap.get("PaymentObjectName"),
        {
          paymentType: "SupplierAdvance",
          payee: state?.supplier?.value,
          "status[in]": "PartiallyAdjusted,Posted",
          queryMode: "Deep",
        },
        {
          enabled: Boolean(state?.supplier?.value),
        }
      )
  );

  useEffect(() => {
    if (paymentData || isLoading) {
      if (!state?.supplier?.value) return;
      if (isLoading && isPaymentSelected) {
        setToastId(toast.loading("Loading Supplier Advance..."));
      }
      if (!paymentData) return;
      const paymentDetailData = formatPayment(paymentData.data);

      if (!paymentDetailData.length) {
        toast.dismiss(toastId);
        showToast("No Supplier Advance found for the given supplier!", "info");
      } else {
        toast.dismiss(toastId);
        showToast("Supplier Advance loaded!", "success");
      }
      setPaymentDetailsTableData(paymentDetailData);
    }

    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [paymentData, state?.supplier?.value, isLoading]);

  const defaultAccounts = useCompanyAccount({
    params: {
      includeCompanyId: true,
      isLinkedWithRecord: false,
    },
  });

  const { data: grnData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("GoodsReceiptNoteObjectName")),
      goodsReceiptNote,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("GoodsReceiptNoteObjectName")),
        goodsReceiptNote
      ),
    {
      enabled: Boolean(goodsReceiptNote),
    }
  );

  useEffect(() => {
    if (grnData) {
      const formattedGrn = formatGrn(grnData);
      setState((prevState) => ({
        ...prevState,
        ...formattedGrn,
      }));
    }
  }, [grnData]);

  const { data: purchaseOrdersData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
      state?.supplier?.value,
    ],
    () =>
      getDynamicObjectRecords(
        kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
        {
          supplier: state?.supplier?.value,
          "status[in]": "Partial,Received",
          "invoiceStatus[in]": "Pending,Partial",
          queryMode: "Deep",
        }
      ),
    {
      enabled: Boolean(state?.supplier?.value && !goodsReceiptNote),
    }
  );

  const { data: purchaseOrderData, isLoading: isLoadingPurchaseOrder } =
    useQuery(
      [
        kebabCase(dynamicObjectMap.get("PurchaseOrderObjectName")),
        purchaseOrderId || state?.purchaseOrder?.value,
      ],
      () =>
        getDynamicObjectRecordById(
          dynamicObjectMap.get("PurchaseOrderObjectName"),
          purchaseOrderId || state?.purchaseOrder?.value
        ),
      {
        enabled:
          Boolean(state?.purchaseOrder?.value) || Boolean(purchaseOrderId),
      }
    );

  useEffect(() => {
    if (
      (purchaseOrdersData?.data &&
        purchaseOrdersData?.data?.length &&
        state?.supplier &&
        defaultAccounts) ||
      isLoadingPurchaseOrder
    ) {
      toast.dismiss(toastId);
      let toastIdValue;
      if (
        isLoadingPurchaseOrder &&
        !isPaymentSelected &&
        state.supplier &&
        !goodsReceiptNote
      ) {
        toastIdValue = toast.loading("Loading Purchase Order...");
        setToastId(toastIdValue);
      }
      if (!purchaseOrdersData?.data) return;
      if (!purchaseOrdersData?.data?.length) {
        showToast("No Purchase Order found for the given supplier!", "info");
        toast.dismiss(toastIdValue);
      } else {
        toast.dismiss(toastIdValue);
        showToast("Purchase Order loaded!", "success");
      }
      const formattedPurchaseOrders = purchaseOrdersData?.data
        .map((r) =>
          formatPurchaseOrder(
            r,
            defaultAccounts?.materialExpense,
            goodsReceiptNote
          )
        );
      toast.dismiss(toastIdValue);
      setPurchaseOrderTableData(formattedPurchaseOrders);
    }

    return () => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    };
  }, [purchaseOrdersData, state?.supplier, defaultAccounts]);

  useEffect(() => {
    if (purchaseOrderData && state?.purchaseOrder && defaultAccounts) {
      const formattedPurchaseOrder = formatPurchaseOrder(
        purchaseOrderData,
        defaultAccounts?.materialExpense,
        goodsReceiptNote
      );
      setInitialState(formattedPurchaseOrder);
    }
  }, [purchaseOrderData, state?.purchaseOrder, defaultAccounts]);

  const setOpenBalance = (total, advanceAmount) => {
    const diff = Number(total || 0) - Number(advanceAmount || 0);
    const openBalance = diff > 0 ? diff : 0;
    ref.current.setFormValue("openBalance", formatDecimalValues(openBalance));
  };

  const prepareLineItem = (key, value, lineItem) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const {
      discountType,
      discount: parentDiscount,
      applyDiscountIndividually,
      subtotal,
    } = formState;
    let discount = parentDiscount;
    let discountAmount = 0;
    const discountRatio = parentDiscount / Number(subtotal);

    const {
      tax,
      quantity: addedQuantity,
      rate,
      remainingQuantity,
      discount: lineItemDiscount,
    } = lineItem;
    let quantity = addedQuantity;

    if (remainingQuantity && quantity > remainingQuantity) {
      quantity = remainingQuantity;
    }

    const amount = quantity * (rate || 0);

    if (applyDiscountIndividually) {
      discount = lineItemDiscount ?? 0;
      discountAmount = lineItemDiscount;
    }

    if (discountType.value === "Amount" && !applyDiscountIndividually) {
      discountAmount = formatDecimalValues(discountRatio * amount);
    }

    if (discountType?.value === "Percentage" && !applyDiscountIndividually) {
      discountAmount = formatDecimalValues(amount * (discount / 100));
    }

    if (discountAmount > amount) {
      discountAmount = amount;
    }

    const selectedTax = tax;
    let discountedAmount = amount - Number(discountAmount || 0);
    let taxAmount = 0;
    if (selectedTax) {
      const parameters = {
        amount: discountedAmount,
        amountOfTax: { value: "Exclusive" },
        tax: selectedTax,
        taxAmount: "",
      };
      const {
        taxAmount: calculatedTaxAmount,
        principalAmount: discountedPrincipalAmount,
      } = calculateTax(parameters);
      taxAmount = calculatedTaxAmount;
      discountedAmount = discountedPrincipalAmount;
    }

    const totalAmount = formatDecimalValues(
      Number(discountedAmount) + Number(taxAmount)
    );

    return {
      ...lineItem,
      quantity,
      discount: discountAmount,
      amount,
      discountedAmount,
      tax: selectedTax,
      taxAmount,
      totalAmount,
    };
  };

  const setTaxAndTotal = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;

    const {
      itemDetail, subtotal, discountType, discount, advanceAmount
    } =
      formState;
    let parentDiscount = discount;

    if (discountType?.value === "Amount") {
      if (discount > subtotal) {
        parentDiscount = subtotal;
      }
    }

    if (discountType?.value === "Percentage") {
      if (discount > 100) {
        parentDiscount = 100;
      }
    }

    if (key === "itemDetailTable") {
      const lineItem = prepareLineItem(key, value, value);
      ref.current.setFormValue("itemDetailTable", lineItem);
    } else {
      const updatedLineItem = itemDetail.map((item) =>
        prepareLineItem(key, value, item)
      );
      const subtotalAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.amount),
        0
      );
      const discountedSubtotal = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.discountedAmount),
        0
      );
      const taxAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.taxAmount),
        0
      );
      const totalAmount = updatedLineItem.reduce(
        (prevValue, currentValue) =>
          Number(prevValue) + Number(currentValue.totalAmount),
        0
      );

      ref.current.setFormState({
        discount: parentDiscount,
        itemDetail: updatedLineItem,
        subtotal: subtotalAmount,
        discountedSubtotal,
        taxAmount,
        total: totalAmount,
        openBalance: totalAmount,
      });
      setOpenBalance(totalAmount, advanceAmount);
    }
  };

  const setDueDate = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { supplier, purchaseOrder } = formState;

    if (!supplier || !purchaseOrder || !value) return;

    let paymentTerm = null;

    if (purchaseOrder && purchaseOrder.paymentTerm) {
      paymentTerm = purchaseOrder.paymentTerm;
    }

    if (supplier && supplier.paymentTerm) {
      paymentTerm = supplier.paymentTerm;
    }

    let dueDate = null;

    if (paymentTerm) {
      const { netDueInDay: paymentTermDueDays } = paymentTerm;

      if (paymentTermDueDays) {
        dueDate = addDays(new Date(value), paymentTermDueDays);
      }
    }

    ref.current.setFormState({
      dueDate,
    });
  };

  const adjustAmountsAndSum = (arr) => {
    let totalAdjustmentAmount = 0;

    arr.forEach((item) => {
      if (item.adjustmentAmount > item.advanceAmount) {
        item.adjustmentAmount = item.advanceAmount;
      }
      totalAdjustmentAmount += Number(item.adjustmentAmount);
    });

    return {
      adjustedArray: arr,
      totalAdjustmentAmount,
    };
  };

  const updateItemDetail = (key, value) => {
    const formState = ref.current.getState();
    formState[key] = value;
    const { asset, itemDetail } = formState;

    if (asset) {
      const updatedItemDetail = itemDetail.map((i) => ({
        asset,
        ...i,
      }));

      ref.current.setFormValue("itemDetail", updatedItemDetail);
    }
  };

  const onStateChange = (key, value) => {
    if (key === "advancePayment") {
      const { total, advanceAmount } = ref.current.getState();
      const result = adjustAmountsAndSum(value);
      ref.current.setFormValue("advanceAmount", result.totalAdjustmentAmount);
      setOpenBalance(total, advanceAmount);
    }

    if (key === "supplier") {
      setState({
        ...state,
        [key]: value,
      });

      if (!value) {
        ref.current.setFormState(purchaseOrderInitialState);
      } else {
        ref.current.setFormState({
          tRN: value?.tRN,
          supplierBalance: value?.openingBalance ?? 0,
        });
      }
    }

    if (key === "purchaseOrder") {
      setState({
        ...state,
        [key]: value,
      });
    }

    if (
      key === "amountOfTax" ||
      key === "discount" ||
      key === "discountType" ||
      key === "itemDetail" ||
      key === "itemDetailTable"
    ) {
      setTaxAndTotal(key, value);
    }

    if (key === "supplierInvoiceDate") {
      setDueDate(key, value);
    }

    if (key === "asset") {
      updateItemDetail(key, value);
    }
  };

  const onChildStateChange = ({
    index,
    key,
    value,
    parentField,
    parentFieldType,
  }) => {
    const formState = ref.current.getState();
    const stateKey = `${parentField}${parentFieldType}`;
    let parentFieldState = formState[stateKey] ?? {};
    if (index > -1) {
      parentFieldState = formState[parentField][index];
    }
    if (parentField === "itemDetail") {
      switch (key) {
        case "item": {
          if (value) {
            const { expenseAccount } = value;
            if (expenseAccount && !isEmpty(expenseAccount)) {
              parentFieldState.account = {
                label: expenseAccount.name,
                value: expenseAccount.id,
              };
            } else {
              parentFieldState.account = state.expenseAccount;
            }
          }
          break;
        }

        case "quantity":
        case "rate":
        case "tax": {
          parentFieldState[key] = value;
          const { quantity, rate, tax } = parentFieldState;

          const amount = formatDecimalValues(quantity * rate);
          parentFieldState.amount = amount;
          parentFieldState.discountedAmount = amount;

          const {
            amountOfTax, discountType, discount, itemDetail
          } = formState;

          if (discountType && discount) {
            const discountAmount = calculateDiscountAmount(
              discountType,
              discount,
              amount,
              itemDetail.length
            );
            parentFieldState.discountAmount = discountAmount;
            parentFieldState.discountedAmount = amount - discountAmount;
          }

          if (tax && amountOfTax) {
            const parameters = {
              amount: parentFieldState.discountedAmount,
              amountOfTax,
              tax,
              taxAmount: "",
            };
            const { taxAmount, principalAmount } = calculateTax(parameters);
            parentFieldState.taxAmount = taxAmount;
            parentFieldState.discountedAmount = principalAmount;
            parentFieldState.totalAmount = formatDecimalValues(
              Number(principalAmount) + Number(taxAmount)
            );
          }
          break;
        }

        default:
          break;
      }
    }
  };

  const handleConfirm = () => {
    if (purchaseOrderTableData.length && !isPaymentSelected) {
      const selectedPurchaseOrder = purchaseOrderTableData.find(
        (q) => q.isSelected
      );
      if (selectedPurchaseOrder) {
        ref.current.setFormState(selectedPurchaseOrder);
      }
    }

    if (paymentDetailsTableData.length && isPaymentSelected) {
      const selectedPayments = paymentDetailsTableData.filter(
        (q) => q.isSelected
      );
      if (selectedPayments.length) {
        const { totalAdjustmentAmount } = adjustAmountsAndSum(selectedPayments);

        const formState = ref.current.getState();
        const { total } = formState;

        let openBalance = 0;

        if (
          total > 0 &&
          totalAdjustmentAmount > 0 &&
          totalAdjustmentAmount < total
        ) {
          openBalance = Number(total) - Number(totalAdjustmentAmount);
        }

        ref.current.setFormState({
          advancePayment: selectedPayments.map((item) => ({
            payment: {
              label: item.number,
              value: item.id,
            },
            advanceAmount:
              Number(item?.total) - Number(item?.adjustedAmount ?? 0),
            adjustmentAmount:
              Number(item?.total) - Number(item?.adjustedAmount ?? 0),
            advanceStatus: {
              label: "Adjusted",
              value: "Adjusted",
            },
          })),
          advanceAmount: totalAdjustmentAmount,
          openBalance,
        });
      }
    }
    closeModal();
  };

  const onBeforeSave = () => {
    const formState = ref.current.getState();
    const {
      advanceAmount,
      total,
      discountType,
      discount,
      subtotal,
      itemDetail,
    } = formState;

    const lineItemsWithZeroTotal = itemDetail.filter(
      (o) => Number(o.totalAmount || 0) === 0
    );

    if (lineItemsWithZeroTotal.length) {
      showToast(
        `Total amount for one or more line items is 0.
         Please enter a larger number or remove the line item from table.`
      );
      return false;
    }

    if (discountType?.value === "Percentage") {
      if (discount >= 100) {
        showToast("A purchase invoice cannot be fully discounted", "error");
        return false;
      }
    }

    if (discount >= subtotal) {
      showToast("A purchase invoice cannot be fully discounted", "error");
      return false;
    }

    if (advanceAmount > (total || 0)) {
      showToast(
        "Total advance amount should be equal to or less than total amount.",
        "error"
      );
      return false;
    }
    return true;
  };

  return (
    <BoxedContent>
      <AlertModal
        icon="file-check-stroke-icon"
        iconClass="success"
        title="Select Supplier Purchase Order"
        subtitle="Selected supplier has following open purchase orders"
        onClose={closeModal}
        isOpen={isOpen}
        onConfirm={handleConfirm}
        size="large"
      >
        {purchaseOrderTableData.length && !isPaymentSelected ? (
          <TableWithCheckbox
            data={purchaseOrderTableData}
            columns={getPurchaseOrderTableColumns()}
            setData={setPurchaseOrderTableData}
          />
        ) : null}
        {paymentDetailsTableData.length && isPaymentSelected ? (
          <TableWithCheckbox
            data={paymentDetailsTableData}
            searchKey={["number"]}
            columns={getExpensePaymentTableColumns()}
            setData={setPaymentDetailsTableData}
            allowMultiple
            selectAll
          />
        ) : null}
      </AlertModal>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        objectName={dynamicObjectMap.get("PurchaseInvoiceObjectName")}
        showHeader
        showLinkedViews
        onStateChange={onStateChange}
        onChildStateChange={onChildStateChange}
        onBeforeSave={onBeforeSave}
        components={{
          HeaderRightContent: (props) =>
            HeaderRightContent({
              openModal,
              isPaymentSelected,
              setIsPaymentSelected,
              showButton:
                Boolean(paymentDetailsTableData.length) ||
                Boolean(purchaseOrderTableData.length),
              ...props,
            }),
        }}
        onSuccess={(newRecordId) =>
          redirect(-1, {
            recordId: newRecordId,
            success: true,
          })}
        navigate={false}
        readOnlyFields={
          isCreatingPIFromPO ?
            ["supplier", "discountType", "discount", "itemDetail"] :
            []
        }
      />
    </BoxedContent>
  );
}

export default PurchaseInvoiceForm;
